import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';

interface PerformanceData {
  period: number;
  totalReturn: number;
  totalReturnUSD: number;
  annualizedReturn: number;
  sharpeRatio: number;
  maxDrawdown: number;
  volatility: number;
  winRate: number;
  totalTrades: number;
  avgTradeReturn: number;
  bestDay: number;
  worstDay: number;
  marketComparison: {
    btcReturn: number;
    ethReturn: number;
    sp500Return: number;
    outperformance: number;
  } | null;
}

interface PerformanceChartProps {
  performanceData?: PerformanceData;
  period: number;
}

const PerformanceChart: React.FC<PerformanceChartProps> = ({ performanceData, period }) => {
  // Generate sample chart data for visualization
  // In a real implementation, this would come from the API
  const generateChartData = () => {
    const data = [];
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - period);
    
    let portfolioValue = 100; // Start at 100%
    let btcValue = 100;
    let ethValue = 100;
    
    // Simulate portfolio performance
    const totalReturn = performanceData?.totalReturn || 0;
    const dailyReturn = totalReturn / period;
    const volatility = (performanceData?.volatility || 15) / 100;
    
    const btcReturn = performanceData?.marketComparison?.btcReturn || 0;
    const ethReturn = performanceData?.marketComparison?.ethReturn || 0;
    const dailyBtcReturn = btcReturn / period;
    const dailyEthReturn = ethReturn / period;
    
    for (let i = 0; i <= period; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + i);
      
      // Add some realistic volatility
      const randomFactor = (Math.random() - 0.5) * volatility * 2;
      portfolioValue *= (1 + (dailyReturn + randomFactor) / 100);
      
      // Simulate benchmark returns
      btcValue *= (1 + (dailyBtcReturn + (Math.random() - 0.5) * 0.02) / 100);
      ethValue *= (1 + (dailyEthReturn + (Math.random() - 0.5) * 0.015) / 100);
      
      data.push({
        date: currentDate.toLocaleDateString(),
        portfolio: Number(portfolioValue.toFixed(2)),
        btc: Number(btcValue.toFixed(2)),
        eth: Number(ethValue.toFixed(2)),
        dateShort: currentDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        })
      });
    }
    
    return data;
  };

  const chartData = generateChartData();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-md">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              ></div>
              <span className="text-sm text-gray-600">{entry.name}:</span>
              <span className="text-sm font-medium text-gray-900">
                {entry.value.toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (!performanceData) {
    return (
      <div className="h-96 flex items-center justify-center bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading performance chart...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Performance Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-lg font-semibold text-gray-900">
            {performanceData.totalReturn.toFixed(2)}%
          </div>
          <div className="text-xs text-gray-500">Total Return</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-lg font-semibold text-gray-900">
            {performanceData.annualizedReturn.toFixed(2)}%
          </div>
          <div className="text-xs text-gray-500">Annualized</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-lg font-semibold text-gray-900">
            {performanceData.bestDay.toFixed(2)}%
          </div>
          <div className="text-xs text-gray-500">Best Day</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-lg font-semibold text-gray-900">
            {performanceData.worstDay.toFixed(2)}%
          </div>
          <div className="text-xs text-gray-500">Worst Day</div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="dateShort" 
              stroke="#666"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              stroke="#666"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="top"
              height={36}
              iconType="line"
              wrapperStyle={{ fontSize: '12px' }}
            />
            
            {/* Reference line at 100% (break-even) */}
            <ReferenceLine y={100} stroke="#e5e5e5" strokeDasharray="2 2" />
            
            {/* Portfolio performance line */}
            <Line
              type="monotone"
              dataKey="portfolio"
              stroke="#3b82f6"
              strokeWidth={3}
              dot={false}
              name="Portfolio"
              activeDot={{ r: 4, fill: '#3b82f6' }}
            />
            
            {/* Benchmark lines */}
            {performanceData.marketComparison && (
              <>
                <Line
                  type="monotone"
                  dataKey="btc"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={false}
                  name="BTC"
                  strokeDasharray="5 5"
                />
                <Line
                  type="monotone"
                  dataKey="eth"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  dot={false}
                  name="ETH"
                  strokeDasharray="3 3"
                />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Performance Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6 text-sm">
        <div className="text-center">
          <div className="font-medium text-gray-900">
            {performanceData.marketComparison?.outperformance.toFixed(2) || '0.00'}%
          </div>
          <div className="text-gray-500">vs Benchmarks</div>
        </div>
        <div className="text-center">
          <div className="font-medium text-gray-900">
            {performanceData.sharpeRatio.toFixed(2)}
          </div>
          <div className="text-gray-500">Sharpe Ratio</div>
        </div>
        <div className="text-center sm:col-span-1 col-span-2">
          <div className="font-medium text-gray-900">
            -{performanceData.maxDrawdown.toFixed(2)}%
          </div>
          <div className="text-gray-500">Max Drawdown</div>
        </div>
      </div>

      {/* Market Comparison Table */}
      {performanceData.marketComparison && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Benchmark Comparison</h4>
          <div className="overflow-hidden bg-gray-50 rounded-lg">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Asset
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Return
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Difference
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-2 text-sm text-gray-900">Bitcoin (BTC)</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">
                    {performanceData.marketComparison.btcReturn.toFixed(2)}%
                  </td>
                  <td className={`px-4 py-2 text-sm text-right font-medium ${
                    (performanceData.totalReturn - performanceData.marketComparison.btcReturn) >= 0
                      ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {(performanceData.totalReturn - performanceData.marketComparison.btcReturn) >= 0 ? '+' : ''}
                    {(performanceData.totalReturn - performanceData.marketComparison.btcReturn).toFixed(2)}%
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-sm text-gray-900">Ethereum (ETH)</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">
                    {performanceData.marketComparison.ethReturn.toFixed(2)}%
                  </td>
                  <td className={`px-4 py-2 text-sm text-right font-medium ${
                    (performanceData.totalReturn - performanceData.marketComparison.ethReturn) >= 0
                      ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {(performanceData.totalReturn - performanceData.marketComparison.ethReturn) >= 0 ? '+' : ''}
                    {(performanceData.totalReturn - performanceData.marketComparison.ethReturn).toFixed(2)}%
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-sm text-gray-900">S&P 500</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">
                    {(performanceData.marketComparison.sp500Return || 0).toFixed(2)}%
                  </td>
                  <td className={`px-4 py-2 text-sm text-right font-medium ${
                    (performanceData.totalReturn - (performanceData.marketComparison.sp500Return || 0)) >= 0
                      ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {(performanceData.totalReturn - (performanceData.marketComparison.sp500Return || 0)) >= 0 ? '+' : ''}
                    {(performanceData.totalReturn - (performanceData.marketComparison.sp500Return || 0)).toFixed(2)}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceChart; 